export type CounterFormat = { digits: number; decimals: number };

export class WaterRegisterFormatHelper {
  static readonly DEFAULT_DIGITS = 5;
  static readonly DEFAULT_DECIMALS = 3;

  static clampDigits(digits: any, min = 1, max = 8): number {
    const n = Number(digits);
    if (!Number.isFinite(n)) return this.DEFAULT_DIGITS;
    return Math.max(min, Math.min(max, Math.trunc(n)));
  }

  static clampDecimals(decimals: any, min = 0, max = 3): number {
    const n = Number(decimals);
    if (!Number.isFinite(n)) return this.DEFAULT_DECIMALS;
    return Math.max(min, Math.min(max, Math.trunc(n)));
  }

  static normalizeFormat(format?: Partial<CounterFormat>): CounterFormat {
    return {
      digits: this.clampDigits(format?.digits),
      decimals: this.clampDecimals(format?.decimals),
    };
  }

  /** 10^decimals */
  static scale(decimals: number): number {
    const d = this.clampDecimals(decimals);
    return Math.pow(10, d);
  }

  /**
   * Convierte un valor humano (ej: "5000.220") al entero escalado según decimals.
   * - Acepta coma o punto.
   * - Vacío => null
   */
  static parseHumanToScaledInt(input: any, decimals: number): number | null {
    if (input === null || input === undefined) return null;

    const raw = String(input).trim();
    if (!raw) return null;

    const normalized = raw.replace(',', '.');
    const num = Number(normalized);
    if (!Number.isFinite(num)) return null;

    const s = this.scale(decimals);
    // Redondeo al entero escalado (no "toFixed" visual, sino para storage)
    return Math.round(num * s);
  }

  /** Parte entera + parte decimal (ya escalada) a partir del entero escalado */
  static splitScaledInt(registerInt: number, decimals: number): { intPart: number; decPart: number } {
    const d = this.clampDecimals(decimals);
    const s = this.scale(d);

    const safe = Number.isFinite(registerInt) ? Math.trunc(registerInt) : 0;

    // Soporta valores negativos (por si acaso)
    const abs = Math.abs(safe);
    const intPart = Math.trunc(abs / s);
    const decPart = abs % s;

    return { intPart: safe < 0 ? -intPart : intPart, decPart };
  }

  /** Padding para la parte entera (digits) */
  static padIntPart(intPart: number, digits: number): string {
    const d = this.clampDigits(digits);
    const abs = Math.abs(intPart);
    const base = String(abs).padStart(d, '0');
    return intPart < 0 ? `-${base}` : base;
  }

  /** Padding para la parte decimal (decimals) */
  static padDecPart(decPart: number, decimals: number): string {
    const d = this.clampDecimals(decimals);
    if (d === 0) return '';
    return String(Math.abs(decPart)).padStart(d, '0');
  }

  /**
   * Formatea el entero escalado según digits/decimals.
   * Ej: newRegisterInt=5000220, digits=8, decimals=3 => 00005000.220
   */
  static formatScaledInt(registerInt: number, format: CounterFormat): string {
    const fmt = this.normalizeFormat(format);
    const parts = this.splitScaledInt(registerInt, fmt.decimals);

    const left = this.padIntPart(parts.intPart, fmt.digits);

    if (fmt.decimals === 0) return left;

    const right = this.padDecPart(parts.decPart, fmt.decimals);
    return `${left}.${right}`;
  }

  /**
   * Etiqueta de formato “8 dígitos / 3 decimales”
   */
  static formatLabel(format: CounterFormat): string {
    const fmt = this.normalizeFormat(format);
    return `${fmt.digits} dígitos / ${fmt.decimals} decimales`;
  }
}